import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { clearPendingClaim } from '@/lib/dealerClaimResume';
import { debugError, debugLog } from '@/utils/debugLogger';

/**
 * Rete di sicurezza per l'attivazione dealer.
 *
 * Se il link ricevuto per email non porta l'utente su `/attiva/completa`
 * (es. redirect non in allow-list Supabase → fallback alla Site URL), il claim
 * già verificato via OTP resterebbe bloccato. Questo hook, una volta per sessione,
 * chiede al database di completare l'eventuale claim verificato associato
 * all'email dell'utente autenticato.
 */
export function useDealerClaimAutoResume(isAuthenticated: boolean, enabled: boolean) {
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (!enabled || !isAuthenticated || ran.current) return;
    if (typeof window === 'undefined') return;
    // Su /attiva* la ripresa è già gestita dalle pagine dedicate.
    if (window.location.pathname.startsWith('/attiva')) return;
    ran.current = true;

    const run = async () => {
      try {
        const { data, error } = await supabase.rpc('complete_verified_claim_for_current_user');
        if (error) {
          debugError('dealer', 'auto resume claim error', error, 'useDealerClaimAutoResume');
          return;
        }
        if (!data) return;

        clearPendingClaim();
        // Traccia l'esito lato server: la coda admin mostra l'ultimo tentativo.
        await supabase.rpc('log_claim_completion_attempt', {
          p_claim_id: data as string,
          p_source: 'auto_resume',
          p_success: true,
        });
        debugLog('dealer', 'claim auto-completato', data, 'useDealerClaimAutoResume');
        navigate('/dashboard-dealer?setup=open', { replace: true });
      } catch (err) {
        debugError('dealer', 'auto resume claim exception', err, 'useDealerClaimAutoResume');
      }
    };


    run();
  }, [enabled, isAuthenticated, navigate]);
}
